import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaHome,
  FaUsersCog,
  FaUpload,
  FaSearch,
  FaRobot,
  FaChartLine,
  FaUserCircle,
  FaSignOutAlt
} from "react-icons/fa";

const NAV_ICON_SIZE = 22;

const NAV_ITEMS = [
  { key: "dashboard", label: "Home", icon: FaHome, path: "/admin-home" },
  { key: "groupadmin", label: "Group Administration", icon: FaUsersCog, path: "/create-group" },
  { key: "upload", label: "Upload Documents", icon: FaUpload, path: "/admin-upload-documents" },
  { key: "query", label: "Query Vault", icon: FaSearch, path: "/admin-query-documents" },
  { key: "agentic", label: "Agentic Services", icon: FaRobot, path: "/admin/agentic-services" },
  { key: "analytics", label: "Analytics", icon: FaChartLine, path: "/admin/analytics" },
  { key: "profile", label: "Settings", icon: FaUserCircle, path: "/admin/profile" }
];

const AdminSidebarShell = ({ activeItem = "dashboard", children }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 980);
  const [isMenuOpen, setIsMenuOpen] = useState(() => window.innerWidth >= 980);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 980;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMenuOpen(true);
      } else {
        setIsMenuOpen(false); // default closed on mobile
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = item.key === activeItem;
    const isClickable = Boolean(item.path);

    return (
      <button
        key={item.key}
        type="button"
        onClick={() => {
          if (!item.path) return;
          navigate(item.path);
          if (isMobile) setIsMenuOpen(false);
        }}
        disabled={!isClickable}
        style={{
          ...navItem,
          ...(isMobile ? navItemMobile : null),
          ...(isActive ? activeNavItem : null),
          ...(isClickable ? null : disabledNavItem)
        }}
      >
        <Icon style={{ fontSize: `${NAV_ICON_SIZE}px` }} />
        {item.label}
      </button>
    );
  };

  return (
    <div style={page}>
      <style>
        {`
          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      {/* Admin Space Banner - Separates top nav from sidebar */}
      <div style={adminBadge}>
        Administrator Workspace
      </div>

      <div style={{ ...workspace, ...(isMobile ? workspaceMobile : null) }}>
        <aside style={{ ...sidebar, ...(isMobile ? sidebarMobile : null) }}>
          {isMobile ? (
            <>
              <div style={mobileMenuHeader}>
                <button
                  type="button"
                  style={hamburgerButton}
                  onClick={() => setIsMenuOpen((open) => !open)}
                  aria-label="Toggle Menu"
                >
                  {isMenuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
                </button>
                <strong style={mobileMenuTitle}>Admin Panel</strong>
                <div style={{ width: 42 }} /> {/* Spacer to balance flex-between */}
              </div>
              {isMenuOpen ? (
                <div style={mobileNavOverlay}>
                  <div style={mobileNavList}>
                    {NAV_ITEMS.map(renderNavItem)}
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/");
                        if (isMobile) setIsMenuOpen(false);
                      }}
                      style={{
                        ...navItem,
                        ...(isMobile ? navItemMobile : null),
                      }}
                    >
                      <FaSignOutAlt style={{ fontSize: `${NAV_ICON_SIZE}px` }} />
                      Logout
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <h2 style={sidebarTitle}>Admin Panel</h2>
              {NAV_ITEMS.map(renderNavItem)}
              {/* Logout Button */}
              <button
                type="button"
                onClick={() => navigate("/")}
                style={{ ...navItem, marginTop: "auto" }}
              >
                <FaSignOutAlt style={{ fontSize: `${NAV_ICON_SIZE}px` }} />
                Logout
              </button>
            </>
          )}
        </aside>

        <main style={{ ...mainContent, ...(isMobile ? mainContentMobile : null) }}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminSidebarShell;

const page = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f7fbff 0%, #edf3f8 100%)",
  width: "100%",
  overflowX: "hidden",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column"
};

const adminBadge = {
  minHeight: "48px",
  padding: "0 26px",
  display: "flex",
  alignItems: "center",
  background: "#edf4fb",
  color: "#0E5580",
  fontSize: "14px",
  fontWeight: "600",
  letterSpacing: "0.2px",
  borderBottom: "1px solid #d5e2ee"
};

const workspace = {
  display: "flex",
  flex: 1,
  gap: "20px",
  padding: "20px",
  width: "100%",
  boxSizing: "border-box",
  position: "relative",
  alignItems: "flex-start"
};

const workspaceMobile = {
  flexDirection: "column"
};

const sidebar = {
  width: "312px",
  background: "#0E5580",
  color: "#fff",
  padding: "24px 18px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  borderRadius: "18px",
  border: "1px solid rgba(255,255,255,0.12)",
  boxShadow: "0 10px 24px rgba(7,38,58,0.18)",
  zIndex: 10,
  alignSelf: "flex-start"
};

const sidebarMobile = {
  width: "100%",
  padding: "12px 14px",
  gap: "0",
  boxSizing: "border-box",
  position: "relative",
  zIndex: 50,
  borderRadius: "14px",
  boxShadow: "0 6px 16px rgba(0,0,0,0.14)"
};

const mobileMenuHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%"
};

const mobileMenuTitle = {
  fontSize: "18px",
  fontWeight: "600",
  color: "#fff"
};

const hamburgerButton = {
  background: "transparent",
  border: "none",
  color: "#fff",
  padding: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "8px",
  transition: "background 0.2s"
};

const mobileNavOverlay = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  background: "#0E5580",
  padding: "0 16px 20px",
  boxShadow: "0 10px 15px rgba(0,0,0,0.15)",
  borderBottomLeftRadius: "16px",
  borderBottomRightRadius: "16px",
  animation: "slideDown 0.2s ease-out forwards",
  transformOrigin: "top",
  zIndex: 40
};

const mobileNavList = {
  display: "grid",
  gap: "10px",
  marginTop: "10px"
};

const sidebarTitle = {
  margin: "0 0 18px",
  fontSize: "24px",
  fontWeight: 600,
  borderBottom: "1px solid rgba(255,255,255,0.22)",
  paddingBottom: "12px"
};

const navItem = {
  border: "none",
  padding: "13px 14px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  borderRadius: "12px",
  cursor: "pointer",
  fontSize: "16px",
  lineHeight: "1.4",
  fontWeight: 500,
  background: "rgba(255,255,255,0.04)",
  whiteSpace: "nowrap",
  color: "#e2e8f0",
  textAlign: "left",
  transition: "all 0.2s ease"
};

const navItemMobile = {
  width: "100%",
  background: "rgba(255,255,255,0.05)"
};

const activeNavItem = {
  background: "rgba(255,255,255,0.18)",
  color: "#fff",
  fontWeight: 600,
  boxShadow: "inset 4px 0 0 #4EA5D9"
};

const disabledNavItem = {
  opacity: 0.5,
  cursor: "not-allowed"
};

const mainContent = {
  flex: 1,
  padding: 0,
  overflowY: "auto",
  minWidth: 0,
  boxSizing: "border-box",
  borderRadius: "18px"
};

const mainContentMobile = {
  padding: 0,
  marginTop: "12px"
};
